import {
  MyChartOpenSchedulingConfig,
  MyChartOpenSchedulingOverrides,
  ProviderMatchProviderV9
} from '@kyruus/types';
import { normalizeUrl } from '../../utilities';
import { ExternalIdSuffixes, ExternalSystemTypes } from './types';

export interface MyChartOpenSchedulingUrlOptions {
  url: string;
  apiKey?: string;
  widgetId?: string;
  department?: string;
  providerId: string;
  visitTypes: string[];

  overrides?: MyChartOpenSchedulingOptionsOverrides;
}

export interface MyChartOpenSchedulingOptionsOverrides {
  embeddedPath?: string;
  loginPath?: string;
  loginUrl?: string;
  visitTypeParameterName?: string;
  visitTypeOverride?: string[];
  apiKeyParameterName?: string;
  widgetIdParameterName?: string;
  departmentParameterName?: string;
  providerIdParameterName?: string;
}

export function convertOverrides(
  overrides: MyChartOpenSchedulingOverrides
): MyChartOpenSchedulingOptionsOverrides {
  return {
    loginPath: overrides.login_path,
    loginUrl: overrides.login_url,
    embeddedPath: overrides.embedded_path,
    apiKeyParameterName: overrides.api_key_parameter_name,
    departmentParameterName: overrides.department_parameter_name,
    providerIdParameterName: overrides.provider_id_parameter_name,
    visitTypeOverride: overrides.visit_type_overrides,
    visitTypeParameterName: overrides.visit_type_parameter_name,
    widgetIdParameterName: overrides.widget_id_parameter_name
  };
}

/**
 * Builds the URL for the MyChart Open Scheduling Widget.
 *
 * @param options - The options to configure the URL.
 * @param options.url - The base URL for the MyChart Open Scheduling Widget (required).
 * @param options.apiKey - The API key for authorization (optional).
 * @param options.widgetId - The widget id for the widget (optional).
 * @param options.department - The department identifier (optional).
 * @param options.visitTypes - An array of visit types.
 * @param options.providerId - The provider id.
 * @param options.overrides - Optional overrides for parameter names and paths.
 * @param options.overrides.embeddedPath - Override for the embedded path (default: 'openscheduling/embedded').
 * @param options.overrides.apiKeyParameterName - Override for the API key parameter name (default: 'apiKey').
 * @param options.overrides.widgetIdParameterName - Override for the widget ID parameter name (default: 'widgetId').
 * @param options.overrides.departmentParameterName - Override for the department parameter name (default: 'department').
 * @param options.overrides.visitTypeParameterName - Override for the visit type parameter name (default: 'visitType').
 * @param options.overrides.providerIdParameterName - Override for the provider ID parameter name (default: 'providerId').
 *
 * @returns The constructed URL as a string.
 */
export function buildMyChartOpenSchedulingWidgetUrl(
  options: MyChartOpenSchedulingUrlOptions
) {
  const embeddedPath =
    options.overrides?.embeddedPath ?? 'openscheduling/embedded';

  const output = new URL(embeddedPath, normalizeUrl(options.url));

  const baseUrlWithPath = output.toString();

  const customUrlSearchParams = new OpenSchedulingQueryStringBuilder();

  const apiKeyParameterName =
    options.overrides?.apiKeyParameterName ?? 'apiKey';

  if (options.apiKey) {
    customUrlSearchParams.set(apiKeyParameterName, options.apiKey);
  }

  const widgetIdParameterName =
    options.overrides?.widgetIdParameterName ?? 'widgetId';

  if (options.widgetId) {
    customUrlSearchParams.set(widgetIdParameterName, options.widgetId);
  }

  if (options.department) {
    const departmentParameterName =
      options.overrides?.departmentParameterName ?? 'department';

    customUrlSearchParams.set(
      departmentParameterName,
      options.department ?? ''
    );
  }

  const visitTypeParameterName =
    options.overrides?.visitTypeParameterName ?? 'visitType';

  const distinctVisitTypes: string[] = [];
  for (const visitType of options.visitTypes) {
    if (!distinctVisitTypes.includes(visitType)) {
      distinctVisitTypes.push(visitType);
    }
  }

  if (distinctVisitTypes?.length > 0) {
    customUrlSearchParams.set(visitTypeParameterName, distinctVisitTypes);
  }

  const providerIdParameterName =
    options.overrides?.providerIdParameterName ?? 'providerId';

  customUrlSearchParams.set(
    providerIdParameterName,
    options.providerId.toString()
  );

  return baseUrlWithPath + customUrlSearchParams.toString();
}

/**
 * Retrieves the visit types from a provider's appointment EHR purposes.
 *
 * This function extracts the visit types that are marked as 'direct_book' from the provider's
 * appointment EHR purposes. If no visit types are found, it falls back to the default visit type
 * specified in the open scheduling configuration.
 *
 * @param {MyChartOpenSchedulingConfig} openSchedulingConfig - The configuration for open scheduling, which may contain a default visit type.
 * @param {ProviderMatchProviderV9} provider - The provider object containing appointment EHR purposes.
 * @returns {string[]} An array of visit type ids.
 */
export function getVisitTypesFromProviderForOpenScheduling(
  openSchedulingConfig: MyChartOpenSchedulingConfig,
  provider: ProviderMatchProviderV9
) {
  if (openSchedulingConfig.overrides?.visit_type_overrides) {
    if (openSchedulingConfig.overrides?.visit_type_overrides.length > 0) {
      return [...openSchedulingConfig.overrides.visit_type_overrides];
    }
  }

  const visitTypes = [];

  if (provider.appointment_ehr_purposes) {
    for (const ehr_purposes of provider.appointment_ehr_purposes) {
      if (ehr_purposes.ehr_data) {
        for (const value of ehr_purposes.ehr_data) {
          if (value?.visibilities?.consumer === 'direct_book') {
            if (value.book_as_type_id) {
              visitTypes.push(value.book_as_type_id);
            }
          }
        }
      }
    }
  }

  if (visitTypes.length === 0) {
    if (openSchedulingConfig.default_visit_type) {
      visitTypes.push(openSchedulingConfig.default_visit_type);
    }
  }

  return visitTypes;
}

/**
 * Retrieves the provider id from the external systems associated with a provider
 * for open scheduling, specifically looking for the `epic` system type.
 *
 * @param provider - The provider object containing external systems information.
 * @returns The provider id from the `epic` external system, or null if not found.
 */
export function getExternalSystemProviderIdForOpenScheduling(
  provider: ProviderMatchProviderV9
) {
  if (!provider.external_systems) {
    return null;
  }

  const epicExternalSystem = provider.external_systems.find(
    (externalSystem) => externalSystem.system_type === ExternalSystemTypes.Epic
  );

  if (epicExternalSystem) {
    return epicExternalSystem.provider_id;
  }

  return null;
}

/**
 * Retrieves the external id for open scheduling from a provider object.
 *
 * This function extracts and validates the external id from the provider's
 * `external_id` property. It ensures that the external id is present and
 * that it has the correct suffix for Epic Open Scheduling (`epic_osw`).
 *
 * @param {ProviderMatchProviderV9} provider - The provider object containing the external id.
 * @returns {string | null} The external id if valid, otherwise `null`.
 */
export function getExternalIdForOpenScheduling(
  provider: ProviderMatchProviderV9
) {
  if (!provider.external_id) {
    return null;
  }

  const [externalId, externalIdSuffix] = provider.external_id.split('@');

  if (!externalId) {
    return null;
  }

  if (externalIdSuffix !== ExternalIdSuffixes.EpicOSW) {
    return null;
  }

  return externalId;
}

/**
 * Retrieves the provider identifier for open scheduling.
 *
 * This function attempts to get the provider id from an external system first.
 * If the external system provider id is not available, it falls back to the
 * provider's external id.
 *
 * @param {ProviderMatchProviderV9} provider - The provider object containing
 * the necessary information to retrieve the id.
 * @returns {string} The provider id for open scheduling.
 */
export function getProviderIdForOpenScheduling(
  provider: ProviderMatchProviderV9
) {
  const externalSystemProviderId =
    getExternalSystemProviderIdForOpenScheduling(provider);

  if (externalSystemProviderId) {
    return externalSystemProviderId;
  }

  return getExternalIdForOpenScheduling(provider);
}

/**
 * Constructs the MyChart login URL based on the provided configuration.
 *
 * @param myChartConfig - The configuration object for MyChart open scheduling.
 *   This object must include all required properties defined in `MyChartOpenSchedulingConfig`.
 *   - `url`: The base URL for MyChart.
 *   - `overrides`: Optional overrides for the default paths.
 *
 * @returns The complete login URL as a string.
 */
export function buildMyChartLoginUrl(
  myChartConfig: Required<MyChartOpenSchedulingConfig>
) {
  if (myChartConfig.overrides?.login_url) {
    return myChartConfig.overrides.login_url;
  }

  const loginPath =
    myChartConfig.overrides?.login_path ?? 'Authentication/Login';

  return new URL(loginPath, normalizeUrl(myChartConfig.url)).toString();
}

/**
 * Encodes URL search parameters with custom handling for array values.
 *
 * This class provides a way to build URL query strings with proper encoding of keys and values.
 * The Open Scheduling widget expects array values (like visit types) to be passed as comma-separated strings,
 * the default implementation of URLSearchParams url encodes commas and does not meet this requirement.
 * It supports both single string values and arrays of strings, which are joined with commas.
 *
 * @example
 * ```typescript
 * const builder = new OpenSchedulingQueryStringBuilder();
 * builder.set('apiKey', 'my-secret-key');
 * builder.set('visitTypes', ['consultation', 'follow-up']);
 * const queryString = builder.toString(); // "?apiKey=my-secret-key&visitTypes=consultation%2Cfollow-up"
 * ```
 */
export class OpenSchedulingQueryStringBuilder {
  private params: Record<string, string> = {};

  /**
   * Encodes and sets a URL parameter.
   * @param key - The parameter key to be URL-encoded
   * @param value - The parameter value(s). Can be a string or array of strings. Each value will be URL-encoded.
   *                If an array is provided, values will be joined with commas.
   * @example
   * encoder.set('city', 'New York');
   * encoder.set('specialties', ['cardiology', 'neurology']);
   */
  public set(key: string, value: string | string[]) {
    if (Array.isArray(value)) {
      value = value.map(encodeURIComponent).join(',');
    } else {
      value = encodeURIComponent(value);
    }

    this.params[encodeURIComponent(key)] = value;
  }

  public toString() {
    return (
      '?' +
      Object.entries(this.params)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
    );
  }
}
